// Secrets a data link refers to by name (a token, a database gateway key, the key that signs fill fingerprints). A PDF never holds one: it
// holds the name, and this browser's storage holds the value, encrypted with a key that cannot be read back out of the browser (a
// non-extractable AES-GCM key in IndexedDB). Clearing the site's data removes them. The names are listed; the values are never shown.

const DB = "fg.pdf.secrets";
const KEYS = "keys";
const VALUES = "values";
const MASTER = "master";

/** The name of the secret that signs fill fingerprints. */
export const FILL_KEY = "fill-signing-key";

/** A secret name is a short word: letters, digits, `-`, `_` and `.`. */
export const validName = (n: string): boolean => /^[A-Za-z0-9._-]{1,64}$/.test(n);

function open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const r = indexedDB.open(DB, 1);
        r.onupgradeneeded = () => { r.result.createObjectStore(KEYS); r.result.createObjectStore(VALUES, { keyPath: "name" }); };
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error ?? new Error("This browser's storage is not available."));
    });
}

const tx = async <T,>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
    });
};

async function masterKey(create: boolean): Promise<CryptoKey | null> {
    const have = await tx<CryptoKey | undefined>(KEYS, "readonly", (s) => s.get(MASTER) as IDBRequest<CryptoKey | undefined>);
    if (have) return have;
    if (!create) return null;
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await tx(KEYS, "readwrite", (s) => s.put(key, MASTER));
    return key;
}

interface Row { name: string; iv: Uint8Array; data: ArrayBuffer }

export async function listSecrets(): Promise<string[]> {
    try { return ((await tx<string[]>(VALUES, "readonly", (s) => s.getAllKeys() as IDBRequest<string[]>)) as string[]).sort(); } catch { return []; }
}

export async function setSecret(name: string, value: string): Promise<void> {
    if (!validName(name)) throw new Error("A secret name is a short word: letters, digits, - _ and .");
    if (!value) throw new Error("Type the secret's value.");
    const key = (await masterKey(true))!;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(value));
    await tx(VALUES, "readwrite", (s) => s.put({ name, iv, data } satisfies Row));
}

export async function removeSecret(name: string): Promise<void> { await tx(VALUES, "readwrite", (s) => s.delete(name)); }

export async function hasSecret(name: string): Promise<boolean> { return (await listSecrets()).includes(name); }

/** Reads one secret's value (null when it is not set here). */
export async function getSecret(name: string): Promise<string | null> {
    if (!validName(name)) return null;
    try {
        const row = await tx<Row | undefined>(VALUES, "readonly", (s) => s.get(name) as IDBRequest<Row | undefined>);
        const key = await masterKey(false);
        if (!row || !key) return null;
        return new TextDecoder().decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv: row.iv as BufferSource }, key, row.data));
    } catch { return null; }
}

/** The values of the named secrets, for the request builders (which look them up synchronously). */
export async function snapshot(names: string[]): Promise<(n: string) => string | null> {
    const m = new Map<string, string>();
    for (const n of names.filter(Boolean)) { const v = await getSecret(n); if (v !== null) m.set(n, v); }
    return (n) => m.get(n) ?? null;
}

/** The fill-signing key as bytes, when one is set. */
export async function fillSigningKey(): Promise<Uint8Array | null> {
    const v = await getSecret(FILL_KEY);
    return v ? new TextEncoder().encode(v) : null;
}
